import { Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const PAGE_WINDOW = 10;

export default function Paginate({
  totalPages,
  currentPage,
  basePath,
  firstPageIsBasePath = false,
}) {
  // Hooks must be called unconditionally — before any early returns
  const [windowStart, setWindowStart] = useState(1);

  // Keep the active page inside the visible window whenever currentPage changes
  useEffect(() => {
    setWindowStart((prev) => {
      const windowEnd = prev + PAGE_WINDOW - 1;
      if (currentPage < prev) return currentPage;
      if (currentPage > windowEnd) return currentPage - PAGE_WINDOW + 1;
      return prev;
    });
  }, [currentPage, totalPages]);

  if (totalPages < 1) return null;

  const windowEnd = Math.min(windowStart + PAGE_WINDOW - 1, totalPages);
  const showPrev = windowStart > 1;
  const showNext = windowEnd < totalPages;

  // Flash the arrow that points toward the active page when it's off-screen
  const flashPrev = showPrev && currentPage < windowStart;
  const flashNext = showNext && currentPage > windowEnd;

  // Remove leading/trailing slashes for safe concatenation
  const cleanBase = basePath.replace(/^\/+|\/+$/g, '');
  const buildLink = (page) => {
    if (page === 1 && firstPageIsBasePath) {
      return cleanBase ? `/${cleanBase}` : '/';
    }
    return cleanBase ? `/${cleanBase}/page/${page}` : `/page/${page}`;
  };

  const pages = [];
  for (let p = windowStart; p <= windowEnd; p++) {
    pages.push(p);
  }

  return (
    <Pagination>
      {/* Left arrow — only when there are pages before the window */}
      {showPrev && (
        <Pagination.Prev
          className={`paginate-arrow${flashPrev ? ' paginate-arrow--flash' : ''}`}
          onClick={() => setWindowStart((s) => Math.max(1, s - PAGE_WINDOW))}
        />
      )}

      {pages.map((page) => (
        <Pagination.Item
          key={page}
          active={page === currentPage}
          as={Link}
          to={buildLink(page)}
          href={buildLink(page)}
        >
          {page}
        </Pagination.Item>
      ))}

      {/* Right arrow — only when there are pages after the window */}
      {showNext && (
        <Pagination.Next
          className={`paginate-arrow${flashNext ? ' paginate-arrow--flash' : ''}`}
          onClick={() =>
            setWindowStart((s) => Math.min(totalPages - PAGE_WINDOW + 1, s + PAGE_WINDOW))
          }
        />
      )}
    </Pagination>
  );
}